import { forwardRef, useId } from 'react'
import { cn } from '../../lib/cn'

interface SelectOption {
  value: string
  label: string
}

interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label?: string
  error?: string
  options: SelectOption[]
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ label, error, options, className, id, ...props }, ref) => {
    const generatedId = useId()
    const selectId = id || generatedId

    return (
      <div className="space-y-1.5">
        {label && (
          <label htmlFor={selectId} className="block text-sm font-medium text-text">
            {label}
          </label>
        )}
        <select
          ref={ref}
          id={selectId}
          className={cn(
            // [color-scheme:dark]: the UA paints the native option popup dark —
            // many platforms ignore option bg/color, which would render the
            // parchment list dark-on-light (or worse) on this warm-dark theme.
            'block w-full [color-scheme:dark] rounded-control bg-surface-sunken px-3 py-2.5 text-base text-text',
            'ring-1 ring-border transition duration-200 ease-out focus:outline-none focus:ring-2 focus:ring-ember-400/50',
            'disabled:opacity-50 disabled:cursor-not-allowed',
            error && 'ring-danger/60',
            className,
          )}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? `${selectId}-error` : undefined}
          {...props}
        >
          {options.map((opt) => (
            <option key={opt.value} value={opt.value} className="bg-surface text-text">
              {opt.label}
            </option>
          ))}
        </select>
        {error && (
          <p id={`${selectId}-error`} className="text-xs text-danger-fg">
            {error}
          </p>
        )}
      </div>
    )
  },
)

Select.displayName = 'Select'
